import React, { useState, useEffect, useContext, useRef } from "react";
import { API_URL } from "../components/api";
import "bootstrap/dist/css/bootstrap.css";
import Axios from "axios";
import Select from "react-select";
import AuthContext from "../context/AuthContext";

function IPTVSub(props) {
  const { user } = useContext(AuthContext);
  const authTokens = JSON.parse(localStorage.getItem("authTokens"));

  // IPTV Subscriptions table
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Onboarding data (clients + usernames)
  const [clientsRaw, setClientsRaw] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [usernameOptions, setUsernameOptions] = useState([]);

  // Current selections
  const [client, setClient] = useState(null);
  const [username, setUsername] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Delete flow
  const [deletingId, setDeletingId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingRow, setPendingRow] = useState(null);

  const isMountedRef = useRef(true);

  const authHeaders = {
    Authorization: `Bearer ${authTokens?.access}`,
    "Content-Type": "application/json",
  };

  const getInternetIptvId = (row) =>
    row?.internet_iptv_id ??
    row?.iptv_subscription_id ??
    row?.iptv_subscription_id ??
    row?.iptv_id ??
    null;

  const fetchSubs = async () => {
    try {
      setLoading(true);
      const response = await Axios.get(
        `${API_URL}/radius/subscriptions/iptv/sub/`,
        {
          headers: authHeaders,
          params: {
            username: user?.username,
            iptv_id: props.iptv_id,
          },
        }
      );

      if (response.status === 200 && isMountedRef.current) {
        const data = Array.isArray(response.data)
          ? response.data
          : response.data
          ? [response.data]
          : [];
        setRows(data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const fetchOnboarding = async () => {
    try {
      const res = await Axios.get(
        `${API_URL}/radius/subscriptions/clients/onboarding/`,
        { headers: authHeaders, params: { username: user?.username } }
      );

      if (res.status === 200 && isMountedRef.current) {
        const rows = res.data || [];
        setClientsRaw(rows);

        const uniqueClients = [
          ...new Map(
            rows.map((item) => [
              item.client_id,
              { label: item.fullname, value: item.client_id },
            ])
          ).values(),
        ];
        setClientOptions(uniqueClients);
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    fetchSubs();
    fetchOnboarding();
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authTokens?.access, user?.username, props.iptv_id]);

  const handleClientChange = (opt) => {
    setClient(opt);
    setUsername(null);

    if (!opt) {
      setUsernameOptions([]);
      return;
    }

    const filtered = clientsRaw
      .filter((row) => row.client_id === opt.value)
      .map((row) => ({
        label: row.username,
        value: row.subscription_id,
      }));

    const uniqueUsernames = [
      ...new Map(filtered.map((u) => [u.label, u])).values(),
    ];
    setUsernameOptions(uniqueUsernames);
  };

  const handleLinkSubmit = async () => {
    if (!client || !username) return;
    setSubmitting(true);

    try {
      await Axios.post(
        `${API_URL}/radius/subscriptions/iptv/sub/`,
        {
          Action: "Post",
          iptv_id: props.iptv_id,
          subscription_id: username.value,
          userdealer: user?.username,
        },
        { headers: authHeaders }
      );

      await fetchSubs();
    } catch (err) {
      console.error("Failed to link IPTV subscription:", err);
    } finally {
      setSubmitting(false);
    }
  };

  // Open confirmation
  const openConfirm = (row) => {
    setPendingRow(row);
    setShowConfirm(true);
  };

  // Confirmed unlink
  const handleUnlinkConfirmed = async () => {
    const row = pendingRow;
    setShowConfirm(false);
    if (!row) return;

    const internetIptvId = getInternetIptvId(row);
    if (!internetIptvId) return;

    setDeletingId(internetIptvId);
    try {
      await Axios.post(
        `${API_URL}/radius/subscriptions/iptv/sub/`,
        {
          Action: "Put",
          internet_iptv_id: internetIptvId,
          userdealer: user?.username,
        },
        { headers: authHeaders }
      );

      await fetchSubs();
    } catch (err) {
      console.error("Failed to unlink IPTV subscription:", err);
    } finally {
      setDeletingId(null);
      setPendingRow(null);
    }
  };

  const customFilter = (option, inputValue) => {
    const words = inputValue.toLowerCase().split(" ").filter(Boolean);
    return words.every((w) => option.label.toLowerCase().includes(w));
  };

  const filteredRows = (() => {
    if (!client && !username) return rows;
    if (username?.label) {
      return rows.filter(
        (r) => (r.username || "").toLowerCase() === username.label.toLowerCase()
      );
    }
    if (client) {
      const allowedUsernames = new Set(
        clientsRaw
          .filter((row) => row.client_id === client.value)
          .map((row) => String(row.username).toLowerCase())
      );
      return rows.filter((r) =>
        allowedUsernames.has(String(r.username || "").toLowerCase())
      );
    }
    return rows;
  })();

  return (
    <div className="testii">
      <div className="mod">
        {/* Selection filters */}
        <div className="row g-2 mb-3">
          <div className="col-md-6">
            <label className="form-label">Client</label>
            <Select
              className="search-line"
              placeholder="Choose Client"
              options={clientOptions}
              onChange={handleClientChange}
              filterOption={customFilter}
              isSearchable
              value={client}
              isClearable
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Username</label>
            <Select
              className="search-line"
              placeholder={client ? "Choose Username" : "Select a client first"}
              options={usernameOptions}
              onChange={setUsername}
              filterOption={customFilter}
              isSearchable
              value={username}
              isDisabled={!client || !usernameOptions.length}
              isClearable
            />
          </div>

          <div className="col-12 mt-3 text-end">
            <button
              className="btn btn-primary"
              type="button"
              disabled={!client || !username || submitting}
              onClick={handleLinkSubmit}
            >
              {submitting ? "Linking..." : "Link IPTV"}
            </button>
          </div>
        </div>

        {/* Table */}
        <table className="table">
          <thead>
            <tr>
              <th>Client Name</th>
              <th>Username</th>
              <th style={{ width: 120 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={3}>Loading…</td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td colSpan={3}>No data</td>
              </tr>
            ) : (
              filteredRows.map((item, idx) => {
                const rowId = getInternetIptvId(item);
                return (
                  <tr key={(item.username || "") + idx}>
                    <td>{item.clients_name ?? "-"}</td>
                    <td>{item.username ?? "-"}</td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => openConfirm(item)}
                        disabled={!rowId || deletingId === rowId}
                        title="Unlink this IPTV subscription"
                      >
                        {deletingId === rowId ? "Deleting…" : "Delete"}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Confirmation popup */}
      {showConfirm && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ background: "rgba(0,0,0,0.5)", zIndex: 1050 }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white rounded shadow"
            style={{ maxWidth: 520, margin: "12vh auto", padding: 20 }}
          >
            <h6 className="mb-2">Confirm unlink</h6>
            <p className="mb-0">
              Are you sure you want to unlink{" "}
              <strong>{pendingRow?.username ?? "-"}</strong> from this IPTV?
            </p>

            <div className="d-flex justify-content-end gap-2 mt-3">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  setShowConfirm(false);
                  setPendingRow(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleUnlinkConfirmed}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default IPTVSub;
